import React from 'react';
import { Code, Rocket, Sparkles } from 'lucide-react';
import { AssessmentResult } from '../types';

interface DetailedSuggestionsProps {
  result: AssessmentResult;
}

export function DetailedSuggestions({ result }: DetailedSuggestionsProps) {
  // 根据用户行业和选择的领域生成针对性建议
  const getIndustrySpecificSuggestions = () => {
    const { userProfile, category } = result;
    const industry = userProfile.industry.primary;

    // 金融行业建议
    if (industry === 'finance') {
      if (category === 'AI' || category === 'both') {
        return {
          technical: {
            title: '智能投研能力',
            suggestions: [
              '掌握GPT/DeepSeek 在金融数据分析中的应用',
              '学习AI在量化交易中的实践',
              '研究大模型在风险评估中的应用'
            ]
          },
          application: {
            title: '场景落地能力',
            suggestions: [
              '咨询AIGC-DAO合作项目实践，共同打造AI+金融行业解决方案',
              '尝试通过AI构建自己的量化交易策略',
              '研究AI在反洗钱合规中的应用'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索AI+RWA资产定价模型创新',
              '研究大模型在金融风控中的应用',
              '关注AI在ESG投资中的实践'
            ]
          }
        };
      } else {
        return {
          technical: {
            title: 'Web3金融能力',
            suggestions: [
              '深入学习DeFi协议设计原理',
              '掌握RWA资产代币化生态及技术原理',
              '研究跨链金融解决方案'
            ]
          },
          application: {
            title: '项目实践能力',
            suggestions: [
              '参与投资香港合规RWA项目',
              '研究探讨数字债券创新',
              '探索资产代币化具体实施'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '研究合规性代币化方案',
              '探索跨境支付新模式',
              '关注监管科技创新'
            ]
          }
        };
      }
    }
    
    // 医疗行业建议
    if (industry === 'healthcare') {
      if (category === 'AI' || category === 'both') {
        return {
          technical: {
            title: '医疗AI能力',
            suggestions: [
              '学习AI医学影像识别技术',
              '研究AI临床辅助诊断系统',
              '通过多模态AI掌握医疗大数据分析'
            ]
          },
          application: {
            title: '落地实践能力',
            suggestions: [
              '尝试利用AI辅助医疗行业论文研究',
              '咨询AIGC-DAO合作项目实践，共同开发"AI+辅助诊断"行业应用',
              '利用多模态AI构建医疗数据分析系统/AI专科医师助理'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索AI+远程医疗方案',
              '研究智能医疗设备创新',
              '关注/探讨医疗元宇宙发展'
            ]
          }
        };
      } else {
        return {
          technical: {
            title: '医疗数据能力',
            suggestions: [
              '学习医疗数据确权技术原理',
              '研究隐私计算解决方案',
              '掌握中医区块链存证技术'
            ]
          },
          application: {
            title: '项目实践能力',
            suggestions: [
              '参与医疗数据交易平台',
              '开发电子处方链上存证',
              '构建医疗数据共享网络'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索医疗区块链应用场景',
              '研究医疗DAO治理模式',
              '关注医疗元宇宙发展'
            ]
          }
        };
      }
    }

    // 制造业建议
    if (industry === 'manufacturing') {
      if (category === 'AI' || category === 'both') {
        return {
          technical: {
            title: '智能制造能力',
            suggestions: [
              '学习工业视觉检测技术',
              '研究预测性维护系统',
              '掌握智能调度算法'
            ]
          },
          application: {
            title: '落地实践能力',
            suggestions: [
              '参与发起智能工厂改造项目',
              '学习构建智能供应链管理系统',
              '尝试AI在供应链优化中的实践，例如工作流优化'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索AI在智能制造中的应用',
              '研究工业元宇宙应用',
              '关注AI+绿色制造创新'
            ]
          }
        };
      } else {
        return {
          technical: {
            title: '供应链Web3能力',
            suggestions: [
              '学习供应链追溯技术',
              '研究资产代币化方案',
              '掌握物联网区块链'
            ]
          },
          application: {
            title: '项目实践能力',
            suggestions: [
              '参与充电桩/广告屏RWA项目',
              '开发供应链金融平台',
              '构建AI智能工业物联网系统'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索工业NFT应用',
              '研究DAO协作模式',
              '关注探讨工业元宇宙'
            ]
          }
        };
      }
    }

    // 教育培训行业建议
    if (industry === 'education') {
      if (category === 'AI' || category === 'both') {
        return {
          technical: {
            title: '智能教育能力',
            suggestions: [
              '掌握AI个性化学习算法',
              '研究智能题库生成技术',
              '学习教育大数据分析方法'
            ]
          },
          application: {
            title: '落地实践能力',
            suggestions: [
              '开发AI辅助教学系统',
              '构建智能学习评估平台',
              '实践AI教育内容生成应用'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索AI+教育元宇宙',
              '研究智能导师系统',
              '关注教育大模型应用'
            ]
          }
        };
      } else {
        return {
          technical: {
            title: 'Web3教育能力',
            suggestions: [
              '学习区块链证书技术',
              '研究去中心化学习平台',
              '掌握教育通证经济模型'
            ]
          },
          application: {
            title: '项目实践能力',
            suggestions: [
              '参与教育NFT项目开发',
              '构建学习成果确权系统',
              '开发技能认证平台'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索Learn-to-Earn模式',
              '研究教育DAO治理',
              '关注教育元宇宙发展'
            ]
          }
        };
      }
    }

    // 文娱创意行业建议
    if (industry === 'entertainment') {
      if (category === 'AI' || category === 'both') {
        return {
          technical: {
            title: '创意AI能力',
            suggestions: [
              '掌握AI内容生成技术',
              '研究多模态创作系统',
              '学习AI音视频处理'
            ]
          },
          application: {
            title: '落地实践能力',
            suggestions: [
              '开发AI创意助手工具',
              '构建智能内容分发平台',
              '实践AI虚拟主播应用'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索AI+创意元宇宙',
              '研究智能IP开发',
              '关注生成式AI应用'
            ]
          }
        };
      } else {
        return {
          technical: {
            title: 'Web3创意能力',
            suggestions: [
              '学习NFT创作技术',
              '研究数字版权保护',
              '掌握创意资产代币化'
            ]
          },
          application: {
            title: '项目实践能力',
            suggestions: [
              '参与数字艺术NFT项目',
              '构建创作者社区平台',
              '开发版权交易系统'
            ]
          },
          innovation: {
            title: '创新探索方向',
            suggestions: [
              '探索Create-to-Earn模式',
              '研究创意DAO运营',
              '关注文娱元宇宙发展'
            ]
          }
        };
      }
    }

    // 科技行业建议
    return {
      technical: {
        title: '核心技术理解力',
        suggestions: [
          '研究自然语言处理在客户服务中的应用',
          '研究区块链底层架构',
          '掌握跨链互操作方案'
        ]
      },
      application: {
        title: '项目实践能力',
        suggestions: [
          '利用开源项目开发垂直领域的AI创新应用',
          '研究AI在网络安全中的应用',
          '构建AI+区块链应用平台'
        ]
      },
      innovation: {
        title: '创新探索方向',
        suggestions: [
          '探索AI结合元宇宙的应用场景',
          '研究去中心化存储',
          '研究大模型在内容生成中的应用'
        ]
      }
    };
  };

  const suggestions = getIndustrySpecificSuggestions();

  return (
    <div className="space-y-6">
      {/* 技术能力建议 */}
      <div className="p-6 bg-gradient-to-r from-blue-900/50 to-cyan-900/50 rounded-2xl border border-white/10">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mr-4">
            <Code className="text-blue-400 w-6 h-6" />
          </div>
          <h4 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400">
            {suggestions.technical.title}
          </h4>
        </div>
        
        <div className="space-y-3">
          {suggestions.technical.suggestions.map((suggestion, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-blue-100/90">
              <span className="text-blue-400">•</span>
              {suggestion}
            </div>
          ))}
        </div>
      </div>

      {/* 应用能力建议 */}
      <div className="p-6 bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-2xl border border-white/10">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mr-4">
            <Rocket className="text-purple-400 w-6 h-6" />
          </div>
          <h4 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
            {suggestions.application.title}
          </h4>
        </div>

        <div className="space-y-3">
          {suggestions.application.suggestions.map((suggestion, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-purple-100/90">
              <span className="text-purple-400">•</span>
              {suggestion}
            </div>
          ))}
        </div>
      </div>

      {/* 创新能力建议 */}
      <div className="p-6 bg-gradient-to-r from-green-900/50 to-emerald-900/50 rounded-2xl border border-white/10">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mr-4">
            <Sparkles className="text-green-400 w-6 h-6" />
          </div>
          <h4 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400">
            {suggestions.innovation.title}
          </h4>
        </div>

        <div className="space-y-3">
          {suggestions.innovation.suggestions.map((suggestion, index) => (
            <div key={index} className="flex items-start gap-2 text-sm text-green-100/90">
              <span className="text-green-400">•</span>
              {suggestion}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}